import ky from 'ky';
import authState from '../store/auth';
import { get } from 'svelte/store';
const BASE_URL = 'https://api.pressone.co/api/';
const BASE_URL_TEST = 'https://pressone-internal-live.herokuapp.com/api/';
const decodeToken = (token) => {
    try {
        // if the token has more or less than 3 parts or is not a string
        // then is not a valid token
        if (token.split('.').length !== 3 || typeof token !== 'string') {
            return null;
        }
        const payload = token.split('.')[1];
        const base64 = payload.replace('-', '+').replace('_', '/');
        const decoded = JSON.parse(atob(base64));
        return decoded;
    }
    catch (error) {
        return null;
    }
};
const isTokenExpired = (token) => {
    const decodedToken = decodeToken(token);
    let result = true;
    if (decodedToken && decodedToken.exp) {
        const expirationDate = new Date(0);
        expirationDate.setUTCSeconds(decodedToken.exp); // sets the expiration seconds
        // compare the expiration time and the current time
        const now = new Date();
        now.setSeconds(now.getSeconds() + 10);
        //a safe bracket of ten seconds
        result = expirationDate.valueOf() < now.valueOf();
    }
    else {
        result = true;
    }
    return result;
};
const publicClient = ky.create({
    // prefixUrl: 'https://api.pressone.co/api/',
    headers: { Accept: 'application/json' },
    // credentials: 'include',
    retry: {
        limit: 0, // no retries
    },
    hooks: {
        afterResponse: [
            async (_req, _opt, response) => {
                if (!response.ok) {
                    response.data = await response.json();
                    return response;
                }
            },
        ],
    },
});
const privateClient = publicClient.extend({
    hooks: {
        beforeRequest: [
            async (request) => {
                const { accessToken, pubKey } = get(authState);
                if (pubKey) {
                    request.headers.set('Pressone-X-Pub-Key', pubKey);
                }
                else if (accessToken) {
                    const isExpired = isTokenExpired(accessToken);
                    let token = accessToken;
                    if (isExpired) {
                        try {
                            const response = await publicClient.post(`auth/token/refresh/`).json();
                            const { token: authToken } = response.data;
                            authState.refresh(authToken);
                            token = authToken;
                        }
                        catch (error) {
                            authState.logout();
                            return;
                        }
                    }
                    request.headers.set('Authorization', `Bearer ${token}`);
                }
                return request;
            },
        ],
    },
});
const PUBLIC_CLIENT = 'public';
const PRIVATE_CLIENT = 'private';
class Client {
    constructor() {
        this.publicClient = publicClient;
        this.privateClient = privateClient;
    }
    getToken() {
        const auth = localStorage.getItem("auth");
        const { accessToken } = (auth && typeof auth === 'string') ? JSON.parse(auth) : { accessToken: auth };
        return accessToken;
    }
    async chromeRequest(method, url, level = 'private', payload = {}, query = {}, useTelephony = false) {
        try {
            const response = await chrome.runtime.sendMessage({
                event: 'makeCORSRequest',
                level, url, method,
                data: method.toUpperCase() === 'GET' ? undefined : payload,
                searchParams: query, useTelephony, token: this.getToken()
            });
            const { status = true } = response;
            if (!status)
                throw response;
            return response;
        }
        catch (error) {
            const { message = "Something went wrong, Please check your internet connection and try again" } = error === null || error === void 0 ? void 0 : error.data;
            throw {
                code: 'INTERNAL_ERROR',
                message, data: null,
                status: false,
            };
        }
    }
    async request(method, __url__, level = 'private', payload, query) {
        const clientToUse = level === PRIVATE_CLIENT ? this.privateClient : this.publicClient;
        const url = __url__.startsWith("http") ? __url__ : (get(authState).mode === 'test' ? BASE_URL_TEST : BASE_URL) + __url__;
        try {
            const response = await clientToUse(url, {
                method,
                searchParams: query,
                json: method.toUpperCase() === 'GET' ? undefined : payload,
            });
            const { status } = response;
            return [204].includes(status) ? response : response.json();
        }
        catch (error) {
            if (error.response) {
                const _error = error.response.data;
                throw _error;
            }
            else {
                const _error = {
                    code: 'INTERNAL_ERROR',
                    message: 'Something went wrong, Please check your internet connection and try again',
                    data: null,
                    status: false,
                };
                throw _error;
            }
        }
    }
    async makeRequest(method, url, level = 'private', payload = {}, query = {}, useTelephony) {
        const type = process.env.APP_PROJECT_TYPE;
        try {
            return (type.toLowerCase() === 'chrome') ? await this.chromeRequest(method, url, level, payload, query, useTelephony) :
                await this.request(method, url, level, payload, query);
        }
        catch (error) {
            throw error;
        }
    }
    async get(...args) {
        return this.makeRequest('get', ...args);
    }
    async post(...args) {
        return this.makeRequest('post', ...args);
    }
    async patch(...args) {
        return this.makeRequest('patch', ...args);
    }
    async put(...args) {
        return this.makeRequest('patch', ...args);
    }
    async delete(...args) {
        return this.makeRequest('delete', ...args);
    }
}
export { PRIVATE_CLIENT, PUBLIC_CLIENT };
export default new Client();
